import {useCallback, useEffect, useMemo, useState} from 'react';

import {Alert, Box, Button, Divider, Stack, TextField, Typography} from '@mui/material';

import {API_CONFIG} from '../../contentGenerator/config';
import {C} from '../../contentGenerator/constants';
import {apiFetch} from '../apiFetch';

const USERS_URL = `${API_CONFIG.PROXY_URL}/users`;
const PASSWORD_MIN_LENGTH = 8;

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
    }

    return payload;
}

function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    }).format(date);
}

const panelSx = {
    border: `1px solid ${C.border}`,
    background: C.surface,
    borderRadius: '12px',
};

export default function UserManagement({currentUser}) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [creating, setCreating] = useState(false);
    const [newUser, setNewUser] = useState({username: '', password: ''});
    const [passwordDrafts, setPasswordDrafts] = useState({});
    const [savingPasswordFor, setSavingPasswordFor] = useState(null);
    const [deletingUserId, setDeletingUserId] = useState(null);

    const currentUserId = currentUser?.id;
    const userCount = users.length;

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiFetch(USERS_URL);
            const data = await parseApiResponse(response, 'Could not load users.');
            setUsers(data.users || []);
        } catch (err) {
            setError(err.message || 'Could not load users.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const sortedUsers = useMemo(
        () => [...users].sort((a, b) => Number(a.id) - Number(b.id)),
        [users],
    );

    const updateNewUser = (field) => (event) => {
        setNewUser((current) => ({...current, [field]: event.target.value}));
    };

    const handleCreateUser = async (event) => {
        event.preventDefault();
        const username = newUser.username.trim();
        if (!username || newUser.password.length < PASSWORD_MIN_LENGTH) {
            setError(`Enter a username and a password with at least ${PASSWORD_MIN_LENGTH} characters.`);
            return;
        }

        setCreating(true);
        setError('');
        setNotice('');
        try {
            const response = await apiFetch(USERS_URL, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({username, password: newUser.password}),
            });
            const data = await parseApiResponse(response, 'Could not create user.');
            setUsers((current) => [...current, data.user]);
            setNewUser({username: '', password: ''});
            setNotice(`User ${data.user.username} created.`);
        } catch (err) {
            setError(err.message || 'Could not create user.');
        } finally {
            setCreating(false);
        }
    };

    const updatePasswordDraft = (userId) => (event) => {
        setPasswordDrafts((current) => ({
            ...current,
            [userId]: event.target.value,
        }));
    };

    const handlePasswordReset = async (userId) => {
        const password = passwordDrafts[userId] || '';
        if (password.length < PASSWORD_MIN_LENGTH) {
            setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
            return;
        }

        setSavingPasswordFor(userId);
        setError('');
        setNotice('');
        try {
            const response = await apiFetch(`${USERS_URL}/${userId}/password`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({password}),
            });
            const data = await parseApiResponse(response, 'Could not update password.');
            setUsers((current) => current.map((user) => (user.id === userId ? data.user : user)));
            setPasswordDrafts((current) => ({...current, [userId]: ''}));
            setNotice(`Password updated for ${data.user.username}.`);
        } catch (err) {
            setError(err.message || 'Could not update password.');
        } finally {
            setSavingPasswordFor(null);
        }
    };

    const handleDeleteUser = async (user) => {
        setDeletingUserId(user.id);
        setError('');
        setNotice('');
        try {
            const response = await apiFetch(`${USERS_URL}/${user.id}`, {method: 'DELETE'});
            await parseApiResponse(response, 'Could not delete user.');
            setUsers((current) => current.filter((entry) => entry.id !== user.id));
            setNotice(`User ${user.username} deleted.`);
        } catch (err) {
            setError(err.message || 'Could not delete user.');
        } finally {
            setDeletingUserId(null);
        }
    };

    return (
        <Box sx={{width: '100%', minHeight: 0, overflow: 'auto'}}>
            <Box sx={{maxWidth: 1180, mx: 'auto', display: 'grid', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
                    <Box>
                        <Typography sx={{fontSize: 22, fontWeight: 800, color: C.ink}}>
                            User management
                        </Typography>
                        <Typography sx={{fontSize: 13, color: C.textSoft, mt: 0.5}}>
                            {userCount} {userCount === 1 ? 'user' : 'users'} in this workspace
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={loadUsers}
                        disabled={loading}
                        sx={{borderColor: C.border2, color: C.ink}}>
                        Refresh
                    </Button>
                </Box>

                {error ? <Alert severity="error" sx={{fontSize: 12}}>{error}</Alert> : null}
                {notice ? <Alert severity="success" sx={{fontSize: 12}}>{notice}</Alert> : null}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: '330px minmax(0, 1fr)'},
                        gap: 2,
                        alignItems: 'start',
                    }}>
                    <Box component="form" onSubmit={handleCreateUser} sx={{...panelSx, p: 2}}>
                        <Stack gap={1.5}>
                            <Typography sx={{fontSize: 15, fontWeight: 800, color: C.ink}}>
                                New user
                            </Typography>
                            <TextField
                                label="Username"
                                size="small"
                                value={newUser.username}
                                onChange={updateNewUser('username')}
                                autoComplete="off"
                                disabled={creating}
                                fullWidth
                            />
                            <TextField
                                label="Password"
                                type="password"
                                size="small"
                                value={newUser.password}
                                onChange={updateNewUser('password')}
                                autoComplete="new-password"
                                disabled={creating}
                                fullWidth
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={
                                    creating ||
                                    !newUser.username.trim() ||
                                    newUser.password.length < PASSWORD_MIN_LENGTH
                                }>
                                {creating ? 'Creating...' : 'Create user'}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={panelSx}>
                        <Box
                            sx={{
                                display: {xs: 'none', lg: 'grid'},
                                gridTemplateColumns: 'minmax(160px, 1fr) 130px minmax(260px, 330px) 90px',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                borderBottom: `1px solid ${C.border}`,
                            }}>
                            {['User', 'Created', 'Password', ''].map((label) => (
                                <Typography
                                    key={label}
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '.08em',
                                        textTransform: 'uppercase',
                                        color: C.textFaint,
                                    }}>
                                    {label}
                                </Typography>
                            ))}
                        </Box>

                        {loading ? (
                            <Typography sx={{p: 2, fontSize: 13, color: C.textSoft}}>
                                Loading users...
                            </Typography>
                        ) : sortedUsers.length ? (
                            sortedUsers.map((user, index) => {
                                const passwordDraft = passwordDrafts[user.id] || '';
                                const isCurrentUser = user.id === currentUserId;
                                const canDelete = !isCurrentUser && userCount > 1;

                                return (
                                    <Box key={user.id}>
                                        {index > 0 ? <Divider /> : null}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr',
                                                    lg: 'minmax(160px, 1fr) 130px minmax(260px, 330px) 90px',
                                                },
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                            }}>
                                            <Box sx={{minWidth: 0}}>
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{minWidth: 0}}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 14,
                                                            fontWeight: 800,
                                                            color: C.ink,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}>
                                                        {user.username}
                                                    </Typography>
                                                    {isCurrentUser ? (
                                                        <Typography
                                                            sx={{
                                                                px: 0.75,
                                                                py: 0.2,
                                                                borderRadius: '6px',
                                                                background: C.tealL,
                                                                color: C.teal,
                                                                fontSize: 10,
                                                                fontWeight: 800,
                                                            }}>
                                                            You
                                                        </Typography>
                                                    ) : null}
                                                </Stack>
                                                <Typography sx={{display: {xs: 'block', lg: 'none'}, mt: 0.5, fontSize: 12, color: C.textSoft}}>
                                                    Created {formatDate(user.createdAt) || 'recently'}
                                                </Typography>
                                            </Box>

                                            <Typography sx={{display: {xs: 'none', lg: 'block'}, fontSize: 12, color: C.textSoft}}>
                                                {formatDate(user.createdAt) || 'recently'}
                                            </Typography>

                                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                                                <TextField
                                                    label="New password"
                                                    type="password"
                                                    size="small"
                                                    value={passwordDraft}
                                                    onChange={updatePasswordDraft(user.id)}
                                                    autoComplete="new-password"
                                                    fullWidth
                                                />
                                                <Button
                                                    variant="outlined"
                                                    disabled={
                                                        savingPasswordFor === user.id ||
                                                        passwordDraft.length < PASSWORD_MIN_LENGTH
                                                    }
                                                    onClick={() => handlePasswordReset(user.id)}
                                                    sx={{borderColor: C.border2, color: C.ink, minWidth: 82}}>
                                                    {savingPasswordFor === user.id ? 'Saving...' : 'Save'}
                                                </Button>
                                            </Stack>

                                            <Button
                                                color="error"
                                                disabled={!canDelete || deletingUserId === user.id}
                                                onClick={() => handleDeleteUser(user)}
                                                sx={{justifySelf: {xs: 'start', lg: 'stretch'}}}>
                                                {deletingUserId === user.id ? 'Deleting...' : 'Delete'}
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography sx={{p: 2, fontSize: 13, color: C.textSoft}}>
                                No users found.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
